import {
  type PublisherDetail,
  publisherActionToStatus,
  type StaffRole,
} from '@planuze/admin-contracts';
import {
  Breadcrumb,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InlineAlert,
  Input,
  Label,
  Page,
  PageContent,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  StatusBadge,
  TableSkeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@planuze/ui';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { CopyableId } from '../components/copyable-id.js';
import { DetailPageState } from '../components/detail-page-state.js';
import { SummaryMetrics } from '../components/detail-summary.js';
import type { StatusAction } from '../components/publishers/publisher-actions.js';
import { PublisherActions } from '../components/publishers/publisher-actions.js';
import { PublisherActivityTable } from '../components/publishers/publisher-activity-table.js';
import { PublisherKeysCard } from '../components/publishers/publisher-keys-card.js';
import { PublisherStatsCell } from '../components/publishers/publisher-stats-cell.js';
import {
  ACTION_VERB,
  statusLabel,
  statusTone,
} from '../components/publishers/publisher-status-map.js';
import { buildPublisherSummaryMetrics } from '../components/publishers/publisher-summary-metrics.js';
import { usePublisherDetailContent } from '../content/sections/publisher-detail.js';
import {
  usePublisherActivity,
  usePublisherDetail,
  useSetPublisherStatus,
  useVerifyPublisher,
} from '../hooks/use-publishers.js';
import { useStaffSession } from '../hooks/use-staff-session.js';
import { fmtDate, fmtDateTime } from '../lib/format.js';

const routeApi = getRouteApi('/authenticated/publishers/$id');
const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'support'];

export function PublisherDetailPage() {
  const { id } = routeApi.useParams();
  const session = useStaffSession();
  const navigate = useNavigate();
  const copy = usePublisherDetailContent();
  const canManage = session !== null && MANAGE_ROLES.includes(session.role);
  const isSuperAdmin = session?.role === 'super_admin';
  const { data: publisher, isLoading, isError } = usePublisherDetail(id);
  const verify = useVerifyPublisher();
  const setStatus = useSetPublisherStatus();

  const renderHeader = (label: string) => (
    <PageHeader>
      <PageTitleBlock>
        <Breadcrumb
          ariaLabel={copy.breadcrumbAriaLabel}
          items={[
            {
              label: 'Publishers',
              onClick: () => {
                navigate({ to: '/publishers' }).catch(() => undefined);
              },
            },
            { label },
          ]}
        />
      </PageTitleBlock>
    </PageHeader>
  );

  if (isError) {
    return (
      <DetailPageState
        header={renderHeader('Publisher')}
        kind="error"
        message={copy.notFoundMessage}
      />
    );
  }
  if (isLoading || publisher === undefined) {
    return <DetailPageState header={renderHeader('Publisher')} kind="loading" message="" />;
  }

  return (
    <PublisherDetailBody
      publisher={publisher}
      canManage={canManage}
      isSuperAdmin={isSuperAdmin}
      verify={verify}
      setStatus={setStatus}
    />
  );
}

function PublisherDetailBody({
  publisher,
  canManage,
  isSuperAdmin,
  verify,
  setStatus,
}: {
  readonly publisher: PublisherDetail;
  readonly canManage: boolean;
  readonly isSuperAdmin: boolean;
  readonly verify: ReturnType<typeof useVerifyPublisher>;
  readonly setStatus: ReturnType<typeof useSetPublisherStatus>;
}) {
  const navigate = useNavigate();
  const copy = usePublisherDetailContent();
  const name = publisher.displayName || publisher.slug || 'Publisher';
  /* Aba ativa controlada: a query de atividade fica lazy (`enabled` só na aba "Atividade"). */
  const [tab, setTab] = useState('overview');
  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <Breadcrumb
            ariaLabel={copy.breadcrumbAriaLabel}
            items={[
              { label: 'Publishers', onClick: () => navigate({ to: '/publishers' }) },
              { label: name },
            ]}
          />
          <PageTitle>{publisher.displayName}</PageTitle>
          <span className="font-mono text-sm text-muted-foreground">{publisher.slug}</span>
        </PageTitleBlock>
      </PageHeader>
      <PageContent className="space-y-6">
        {/* Identidade+chaves+stats+domainVerified vêm juntos da query única `usePublisherDetail`
            (ADR-0476); a aba "Atividade" carrega sob demanda (`usePublisherActivity` lazy, ADR-0466). */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview">{copy.tabs.overview}</TabsTrigger>
            <TabsTrigger value="keys">{copy.tabs.keys}</TabsTrigger>
            <TabsTrigger value="moderation">{copy.tabs.moderation}</TabsTrigger>
            <TabsTrigger value="activity">{copy.tabs.activity}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <SummaryMetrics items={buildPublisherSummaryMetrics(publisher)} />
            <Card>
              <CardContent className="space-y-4 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge variant={statusTone(publisher.status)} dot>
                      {statusLabel(publisher.status, copy.statusLabels)}
                    </StatusBadge>
                    {publisher.domainVerified && (
                      <StatusBadge variant="success" dot>
                        {copy.domainVerifiedBadge}
                      </StatusBadge>
                    )}
                  </div>
                  <GovernanceActions
                    publisher={publisher}
                    canManage={canManage}
                    isSuperAdmin={isSuperAdmin}
                    verify={verify}
                    setStatus={setStatus}
                  />
                </div>
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-muted-foreground">ID</dt>
                    <dd className="text-foreground">
                      <CopyableId value={publisher.id} label={copy.publisherIdLabel} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Slug</dt>
                    <dd className="text-foreground">
                      <CopyableId value={publisher.slug} label="slug" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{copy.fields.createdAt}</dt>
                    <dd className="text-foreground">{fmtDate(publisher.createdAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">{copy.fields.verifiedAt}</dt>
                    <dd className="text-foreground">
                      {publisher.verifiedAt ? fmtDateTime(publisher.verifiedAt) : '—'}
                    </dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="keys">
            <PublisherKeysCard keys={publisher.keys} />
          </TabsContent>

          <TabsContent value="moderation">
            <Card>
              <CardHeader>
                <CardTitle>{copy.tabs.moderation}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <PublisherStatsCell stats={publisher.stats} />
                <div>
                  {/* O filtro por publisher na fila é follow-up (ADR-0476) — por ora, link à fila. */}
                  <Link to="/pack-submissions" className="text-sm text-primary hover:underline">
                    {copy.moderationQueueLink}
                  </Link>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity">
            <PublisherActivityTab publisherId={publisher.id} enabled={tab === 'activity'} />
          </TabsContent>
        </Tabs>
      </PageContent>
    </Page>
  );
}

/* Aba Atividade (ADR-0466): trilha keyset carregada sob demanda (`enabled` = aba ativa). Owner da
   infinite query + "carregar mais"; a `PublisherActivityTable` é presentacional (molde do audit). */
function PublisherActivityTab({
  publisherId,
  enabled,
}: {
  readonly publisherId: string;
  readonly enabled: boolean;
}) {
  const copy = usePublisherDetailContent();
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePublisherActivity(publisherId, enabled);
  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  if (isError) {
    return <InlineAlert variant="destructive">{copy.activityLoadError}</InlineAlert>;
  }
  if (isLoading) {
    return <TableSkeleton columns={5} />;
  }
  return (
    <div className="space-y-4">
      <PublisherActivityTable items={items} />
      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            loading={isFetchingNextPage}
            onClick={() => {
              fetchNextPage().catch(() => undefined);
            }}
          >
            {copy.loadMore}
          </Button>
        </div>
      )}
    </div>
  );
}

/* Barra de governança: verificar (direto) + suspender/banir + reversos (confirmação com motivo,
   Dialog `locked` — ADR-0216). ban/unban só super_admin. */
function GovernanceActions({
  publisher,
  canManage,
  isSuperAdmin,
  verify,
  setStatus,
}: {
  readonly publisher: PublisherDetail;
  readonly canManage: boolean;
  readonly isSuperAdmin: boolean;
  readonly verify: ReturnType<typeof useVerifyPublisher>;
  readonly setStatus: ReturnType<typeof useSetPublisherStatus>;
}) {
  const copy = usePublisherDetailContent();
  const [pending, setPending] = useState<StatusAction | null>(null);
  const [reason, setReason] = useState('');
  const isBusy = setStatus.isPending;

  const onVerify = (pid: string): void => {
    verify.mutate(pid, {
      onSuccess: (r) => toast.success(copy.verifySuccess(r.signedKeyIds.length)),
      onError: () => toast.error(copy.verifyError),
    });
  };
  const closeDialog = (): void => {
    if (isBusy) return;
    setPending(null);
    setReason('');
  };
  const confirm = (): void => {
    if (pending === null || reason.trim().length === 0) return;
    setStatus.mutate(
      { id: publisher.id, status: publisherActionToStatus[pending], reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(copy.actionAppliedSuccess(ACTION_VERB[pending]));
          setPending(null);
          setReason('');
        },
        onError: () => toast.error(copy.actionError(ACTION_VERB[pending])),
      },
    );
  };

  return (
    <>
      <PublisherActions
        publisher={publisher}
        canManage={canManage}
        isSuperAdmin={isSuperAdmin}
        busy={isBusy}
        onVerify={onVerify}
        onRequestAction={(_pub, action) => setPending(action)}
      />
      <Dialog open={pending !== null} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent locked={isBusy}>
          <DialogHeader>
            <DialogTitle>{copy.confirmActionTitle}</DialogTitle>
            <DialogDescription>
              {pending !== null
                ? copy.confirmActionDescription(ACTION_VERB[pending], publisher.displayName)
                : ''}{' '}
              {copy.reasonHint}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="publisher-detail-reason">{copy.reasonLabel}</Label>
            <Input
              id="publisher-detail-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={copy.reasonPlaceholder}
              disabled={isBusy}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeDialog} disabled={isBusy}>
              {copy.cancel}
            </Button>
            <Button
              variant={pending === 'ban' ? 'destructive' : 'default'}
              loading={isBusy}
              disabled={reason.trim().length === 0}
              onClick={confirm}
            >
              {copy.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
